/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';

import { AVERAGE_COST } from 'services/CostBasisMethod';
import { formatAccountNumber } from 'services/Formats';
import { RECEIVED_ACCOUNTS } from 'state/account/AccountBalancesActions';
import {
  RECEIVED_UPDATE_CUSTOMER_PREFERENCES,
  RECEIVED_CUSTOM_ACCOUNT_GROUPS
} from 'state/preferences/PreferencesActions';

import { filterFunctionMapping } from 'state/shared/FilterFunctionMappingUtil';
import {
  RECEIVED_TAXES,
  SELECT_COLLEGE_SAVINGS_FILTER,
  SELECT_TAX_TABLE_FILTER,
  UPDATE_COST_BASIS_METHOD,
  RESET_COST_BASIS_METHOD
} from './TaxActions';
import { RECEIVED_CDS_PORTFOLIO } from "../Actions";
import { hasSmallBusinessRetirementCategory } from "state/taxonomy/TaxonomySelectors";

export const CUSTOM_ACCOUNT_GROUP_PREFIX = 'custom-';

export const priorYearIndicatorInitialState = false;

export const costBasisBalances = (state = [], action) => {
  return action.type === RECEIVED_TAXES
    ? action.payload.costBasisBalances
    : state;
};

const cleanCostBasisElections = (costBasisElections) => {
  /* This will not be necessary for most cases but it is possible
     for the backend to send null. We need to default to Average Cost
     in this case. */
  const cleanedCostBasisElections = [];
  costBasisElections.forEach((costBasisElection) => {
    if (costBasisElection.costBasisTypeId === null) {
      cleanedCostBasisElections.push({
        ...costBasisElection,
        costBasisTypeId: AVERAGE_COST,
        costBasisMethod: 'Average Cost'
      });
    } else {
      cleanedCostBasisElections.push(costBasisElection);
    }
  });
  return cleanedCostBasisElections;
};

export const costBasisElections = (state = [], action) => {
  if (action.type === RECEIVED_TAXES) {
    const { costBasisElections } = action.payload;
    return cleanCostBasisElections(costBasisElections);
  }

  if (action.type === RECEIVED_UPDATE_CUSTOMER_PREFERENCES) {
    const {
      preferences: { costBasisElections }
    } = action;
    if (!isEmpty(costBasisElections)) {
      return cleanCostBasisElections(costBasisElections);
    }
  }

  return state;
};

export const pendingCostBasisElectionsInitialState = [];
export const pendingCostBasisElectionsReducer = (
  state = pendingCostBasisElectionsInitialState,
  action
) => {
  if (action.type === RECEIVED_TAXES) {
    const { costBasisElections } = action.payload;
    return cleanCostBasisElections(costBasisElections);
  }

  if (action.type === RECEIVED_UPDATE_CUSTOMER_PREFERENCES) {
    const {
      preferences: { costBasisElections }
    } = action;
    if (!isEmpty(costBasisElections)) {
      return cleanCostBasisElections(costBasisElections);
    }
  }

  if (action.type === RESET_COST_BASIS_METHOD) {
    const { costBasisElections } = action;
    return [...costBasisElections];
  }

  if (action.type === UPDATE_COST_BASIS_METHOD) {
    const { accountId, costBasisTypeId } = action;
    const costBasisElection = state.find(
      (costBasis) => costBasis.accountDescriptor.accountId === accountId
    );
    const remainingCostBasisElections = state.filter(
      (costBasis) => costBasis.accountDescriptor.accountId !== accountId
    );
    return [
      ...remainingCostBasisElections,
      {
        ...costBasisElection,
        costBasisTypeId
      }
    ];
  }

  return state;
};

export const priorYearIndicatorReducer = (state = false, action) => {
  return action.type === RECEIVED_TAXES
    ? Boolean(action.payload.priorYearIndicator)
    : state;
};

export const ytdTaxInformationInitialState = [];

export const ytdTaxInformationReducer = (
  state = ytdTaxInformationInitialState,
  action
) => {
  return action.type === RECEIVED_TAXES
    ? action.payload.ytdTaxInformation || []
    : state;
};

export const ytdTaxFilteringInitialState = {
  selection: 'all',
  options: [
    {
      label: 'All Accounts',
      value: 'all'
    },
    {
      label: 'Retirement Accounts',
      value: 'retirement'
    },
    {
      label: 'Non-Retirement Accounts',
      value: 'nonRetirement'
    },
    {
      label: 'Owned Accounts',
      value: 'owned'
    }
  ]
};

export const ytdTaxFilteringReducer = (
  state = ytdTaxFilteringInitialState,
  action,
  fullState = {}
) => {
  // TODO: want to modify this reducer to not depend on accounts endpoint and only taxonomy endpoint
  if (action.type === RECEIVED_ACCOUNTS) {
    const { accounts, products } = fullState;
    return {
      ...state,
      options: state.options.filter(({ value }) =>
        accounts.some(filterFunctionMapping(value, products.providers))
      )
    };
  }

  if (action.type === RECEIVED_CDS_PORTFOLIO) {
    // TODO: would include flags for all current default dropdown options - filterFunctionMapping in accounts would be moved here
    const hasSmallBusiness = hasSmallBusinessRetirementCategory(fullState);

    if (hasSmallBusiness) {
      return {
        ...state,
        options: state.options.concat(
            {
              label: 'Small Business Accounts',
              value: 'smallBusiness',
            }
        )
      };

    }
  }

  if (action.type === RECEIVED_CUSTOM_ACCOUNT_GROUPS) {
    const { customAccountGroups } = action;
    if (customAccountGroups && customAccountGroups.length > 0) {
      return {
        ...state,
        options: state.options.concat(
          customAccountGroups.map(({ name, accountIds }) => ({
            label: `${name}`,
            value: `${CUSTOM_ACCOUNT_GROUP_PREFIX}${name}`,
            accountIds
          }))
        )
      };
    }
  }

  if (action.type === SELECT_TAX_TABLE_FILTER) {
    const { selection } = action.payload;
    const accountIds = (
      state.options.find(({ value }) => value === selection) || {}
    ).accountIds;
    return {
      ...state,
      selection,
      accountIds
    };
  }

  return state;
};

export const iraContributionsReducer = (state = [], action) => {
  return action.type === RECEIVED_TAXES
    ? action.payload.iraContributions
    : state;
};

export const iraDistributionsReducer = (state = [], action) => {
  return action.type === RECEIVED_TAXES
    ? action.payload.iraDistributions
    : state;
};

export const iraWithholdingsReducer = (state = [], action) => {
  return action.type === RECEIVED_TAXES
    ? action.payload.iraWithholdings
    : state;
};
export const collegeSavingsActivitiesReducer = (state = [], action) => {
  return action.type === RECEIVED_TAXES
    ? action.payload.collegeSavingsActivities || []
    : state;
};

export const collegeSavingsFilterInitialState = {
  selection: 'all',
  options: [
    {
      label: 'All Accounts',
      value: 'all'
    }
  ]
};

export const collegeSavingsFilterReducer = (
  state = collegeSavingsFilterInitialState,
  action,
  fullState = {}
) => {
  if (action.type === RECEIVED_TAXES) {
    const { collegeSavingsActivities } = action.payload;
    if (collegeSavingsActivities) {
      const { accountsById, hiddenAccounts } = fullState;
      const newOptions = collegeSavingsActivities
        .map((collegeSavings) => {
          const { accountId } = collegeSavings;
          const isHidden = hiddenAccounts.some(
            (hiddenAccount) => hiddenAccount.accountId === accountId
          );
          if (!isHidden && accountId) {
            const account = accountsById[accountId];
            if (account) {
              const { accountName, accountType } = account;
              const accountWChecksum = formatAccountNumber(account);
              const label = `${accountName} - ${accountWChecksum} - ${accountType}`;
              return {
                label,
                value: accountId
              };
            }
          }
        })
        .filter((a) => !isUndefined(a))
        .sort((a, b) => a.label.localeCompare(b.label));
      return {
        ...state,
        options: [...state.options, ...newOptions]
      };
    }
  }

  if (action.type === SELECT_COLLEGE_SAVINGS_FILTER) {
    const { selection } = action.payload;

    const accountIds = (
      state.options.find(({ value }) => value === selection) || {}
    ).accountIds;
    return {
      ...state,
      selection,
      accountIds
    };
  }

  return state;
};
